import {
  Box,
  Dialog,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CustomDatePickerField from "../../common/DatePicker/DatePicker";
import { groupingStyles } from "../../ExpenseVoucher/ExpenseRequest/ExpenseRequest";
import {
  CommonButton,
  CommonOutlinedButton,
  CustomRequiredInputLabel,
} from "../../common/StyledComponents/StyledFields";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useEffect, useRef, useState } from "react";
import ButtonLoader from "../../common/ButtonLoader/ButtonLoader";
import ErrorPopup from "../../common/ErrorPopup";
import toast from "react-hot-toast";
import {
  apiRequest,
  catchErrorMessageHandler,
  displayDateFormat,
  getNextMonthDate,
  getUserDetails,
  getUserName,
  sortDataByDateAndTime,
} from "../../../infrastructure/backendService";
import apiPath from "../../../config/apiPaths.json";
import appStyles from "../../../App.module.scss";
import styles from "../../devoteeService/priestSchedule/priestSchedule.module.scss";
import { useReactToPrint } from "react-to-print";
import { Close, Fullscreen, FullscreenExit } from "@mui/icons-material";
import CustomSelectField from "../../common/CustomSelectField/CustomSelectField";
import { useAppDispatch, useAppSelector } from "../../../app/hook";
import { fetchPriestList } from "../../../features/poojaBookingSlice";
import DraggableFunction from "../../common/DraggableDialogFunction/DraggableFunction";
import { fetchHallList } from "../../../features/hallBookingSlice";
import PageLoader from "../../common/PageLoader/PageLoader";

const printStyles = `
 @page {
      size: tabloid;
      margin: 24px;
    }
 
    /* Set layout to Landscape */
    body {
      width: 100vw;
      position: absolute;
    }
@media print {
  .printable {
    display: block !important;
  }
}
`;

interface priestScheduleProps {
  isViewPriestSchedule?: boolean;
  isHallSchedule?: boolean;
}

const MySchedule = (props: priestScheduleProps) => {
  const { isViewPriestSchedule, isHallSchedule } = props;
  const componentRef = useRef(null);

  const { priestsList } = useAppSelector((state) => state.poojaBookingDS);
  const { hallList } = useAppSelector((state) => state.hallBooking);
  const dispatch = useAppDispatch();

  const [isBtnLoading, setIsButtonLoading] = useState(false);
  const [mySchedules, setMySchedules] = useState<any>(null);
  const [isAPICalled, setIsAPICalled] = useState(false);
  const [fullScreenView, setFullScreenView] = useState(true);
  const [isExcelLoading, setIsExcelLoading] = useState(false);

  const initialValues = {
    priestId: isViewPriestSchedule ? null : getUserName(),
    startDate: displayDateFormat(new Date()),
    endDate: displayDateFormat(getNextMonthDate(new Date())),
  };

  const validationSchema = Yup.object().shape({
    priestId: Yup.string().required(
      isHallSchedule ? "Hall is required" : "Priest is required"
    ),
    startDate: Yup.string().required("From date is required"),
    endDate: Yup.string()
      .required("To date is required")
      .test(
        "is-greater",
        "To date should not be less than from date",
        function (value) {
          const { startDate } = this.parent;
          if (!value || !startDate) return true; // If either date is not set, skip this validation
          return new Date(value) >= new Date(startDate);
        }
      ),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit(values) {
      setIsButtonLoading(true);
      getMySchedules(values);
    },
    enableReinitialize: true,
  });

  const tableColumns = [
    { id: "date", fieldName: "Date" },
    { id: "startTime", fieldName: "Start Time" },
    { id: "endTime", fieldName: "End Time" },
    { id: "description", fieldName: "Description" },
  ];

  useEffect(() => {
    if (isViewPriestSchedule) {
      if (isHallSchedule) {
        dispatch(fetchHallList());
      } else {
        dispatch(fetchPriestList());
      }
    }
    formik.resetForm();
    setFullScreenView(true);
  }, [isViewPriestSchedule, isHallSchedule]);

  const getMySchedules = async (values: any) => {
    await apiRequest({
      method: "GET",
      endpoint: apiPath.schedules,
      params: {
        scheduleType: isHallSchedule ? "HALL" : "PRIEST",
        schedulesForDay: false,
        ...(isHallSchedule
          ? { hallId: values?.priestId }
          : { priestId: values?.priestId }),
        startDate: displayDateFormat(new Date(values?.startDate)),
        endDate: displayDateFormat(new Date(values?.endDate)),
      },
      includeToken: true,
    })
      .then((res: any) => {
        const sortedSchedules = sortDataByDateAndTime(
          res?.data?.[0]?.schedulesByDate ?? []
        );
        setMySchedules(sortedSchedules ?? []);
        setIsButtonLoading(false);
        setIsAPICalled(true);
      })
      .catch((err: any) => {
        setIsButtonLoading(false);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
        );
      });
  };

  const getPriestDisplayName = () => {
    let priestName = getUserDetails();
    if (isViewPriestSchedule) {
      if (isHallSchedule) {
        priestName =
          hallList?.find(
            (priest: any) => priest?.id === formik?.values?.priestId
          )?.name ?? "";
      } else {
        priestName =
          priestsList?.find(
            (priest: any) => priest?.priest_id === formik?.values?.priestId
          )?.priestname ?? "";
      }
    }

    return priestName;
  };

  const getDialogWidthAndHeight = () => {
    let dialogStyles: any = null;
    if (fullScreenView) {
      dialogStyles = {
        width: "100vw",
        height: "100vh",
        maxWidth: "100vw",
        maxHeight: "100vh",
        borderRadius: "0px",
        margin: "0px",
        transform: "none !important",
        background: "#fff9e4",
        top: "0px",
        left: "0px",
        bottom: "0px",
        right: "0px",
        position: "fixed",
      };
    } else {
      dialogStyles = {
        width: { xs: "100%", md: "90%" },
        maxWidth: { xs: "100%", md: "1200px" },
        maxHeight: {
          xs: "calc(100% - 30px)",
          sm: "calc(100% - 40px)",
        },
        borderRadius: "8px",
        margin: { xs: "15px", sm: "20px" },
      };
    }
    return dialogStyles;
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: printStyles,
    documentTitle: `${getPriestDisplayName()} schedules from ${displayDateFormat(
      new Date(formik?.values?.startDate)
    )} to ${displayDateFormat(new Date(formik?.values?.endDate))}`,
  });

  const handleExportToExcel = async () => {
    const values = formik.values;
    setIsExcelLoading(true);
    apiRequest({
      method: "GET",
      endpoint: `${apiPath.schedules}/excel`,
      params: {
        scheduleType: isHallSchedule ? "HALL" : "PRIEST",
        schedulesForDay: false,
        ...(isHallSchedule
          ? { hallId: values?.priestId }
          : { priestId: values?.priestId }),
        startDate: displayDateFormat(new Date(values?.startDate)),
        endDate: displayDateFormat(new Date(values?.endDate)),
      },
      includeToken: true,
      responseType: "arraybuffer",
    })
      .then((res) => {
        const blob = new Blob([res?.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        link.download = `${getPriestDisplayName()} schedules from ${displayDateFormat(
          new Date(formik?.values?.startDate)
        )} to ${displayDateFormat(new Date(formik?.values?.endDate))}.xlsx`;
        link.href = window.URL.createObjectURL(blob);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setIsExcelLoading(false);
      })
      .catch((err) => {
        setIsExcelLoading(false);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
        );
      });
  };

  const handleChange = (field: string, value: any) => {
    formik.setFieldValue(field, value);
  };

  const handleClose = () => {
    setMySchedules(null);
    setIsAPICalled(false);
    setFullScreenView(true);
  };

  const handleFullScreen = () => {
    setFullScreenView(!fullScreenView);
  };

  const renderFieldsComponent = () => {
    return (
      <Box className="common_box_shadow" sx={{ mt: "20px" }}>
        {isViewPriestSchedule && (
          <Typography
            className="sub_heading"
            sx={{ fontSize: "16px !important", m: "0px 0px 10px 0 !important" }}
          >
            {isHallSchedule ? "Hall" : "Priest"} Schedule (Individual)
          </Typography>
        )}
        {isViewPriestSchedule && (
          <Box
            sx={{
              ...groupingStyles.groupedFieldsContainer,
              mt: "10px",
            }}
          >
            <Box sx={groupingStyles.fieldContainer}>
              {CustomRequiredInputLabel(
                `Select ${isHallSchedule ? "Hall" : "Priest"} Name`,
                true
              )}
              <CustomSelectField
                handleDropdownChange={handleChange}
                errorMessage={
                  formik.touched.priestId ? formik.errors.priestId : ""
                }
                dropDownList={isHallSchedule ? hallList : priestsList}
                value={formik.values.priestId}
                fieldName="priestId"
                placeholder={"Select name"}
                optionLabel={isHallSchedule ? "name" : "priestname"}
                optionValue={isHallSchedule ? "id" : "priest_id"}
              />
            </Box>
          </Box>
        )}
        <Box sx={groupingStyles.groupedFieldsContainer}>
          <Box sx={groupingStyles.fieldContainer}>
            {CustomRequiredInputLabel("From Date", true)}
            <CustomDatePickerField
              date={formik.values.startDate}
              handleDateChange={handleChange}
              errorMessage={
                formik.touched.startDate ? formik.errors.startDate : ""
              }
              name="startDate"
            />
          </Box>
          <Box sx={groupingStyles.fieldContainer}>
            {CustomRequiredInputLabel("To Date", true)}
            <CustomDatePickerField
              date={formik.values.endDate}
              handleDateChange={handleChange}
              errorMessage={formik.touched.endDate ? formik.errors.endDate : ""}
              name="endDate"
            />
          </Box>
        </Box>
        <CommonButton
          onClick={() => {
            !isBtnLoading && formik.handleSubmit();
          }}
          sx={{
            mt: `${
              (formik.touched.startDate && formik.errors.startDate) ||
              (formik.touched.endDate && formik.errors.endDate)
                ? "20px"
                : "5px"
            }`,
          }}
        >
          <ButtonLoader isBtnLoading={isBtnLoading} btnText={"Submit"} />
        </CommonButton>
      </Box>
    );
  };

  const renderDialogContentHeader = () => {
    return (
      <Box
        px={{ xs: "10px", sm: "15px" }}
        pt={{ xs: "10px" }}
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <Box>
          <CommonOutlinedButton
            className={`${styles.week_and_day_buttons} no_print`}
            onClick={handlePrint}
            sx={{
              fontSize: { xs: "12px !important", sm: "14px !important" },
              height: "fit-content !important",
              p: { xs: "5px !important" },
              mr: "10px",
            }}
          >
            Print Report
          </CommonOutlinedButton>
          <CommonOutlinedButton
            className={`${styles.week_and_day_buttons} no_print`}
            onClick={handleExportToExcel}
            sx={{
              fontSize: { xs: "12px !important", sm: "14px !important" },
              height: "fit-content !important",
              p: { xs: "5px !important" },
            }}
          >
            <ButtonLoader
              isBtnLoading={isExcelLoading}
              btnText="Export to Excel"
            />
          </CommonOutlinedButton>
        </Box>
      </Box>
    );
  };

  const renderSchedulesTable = () => {
    return (
      <Box
        className="print_width_height"
        sx={{
          m: { xs: "10px", sm: "10px 15px 15px" },
          overflowX: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#F2BE22", // Scrollbar color
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "white",
          },
        }}
      >
        <Box
          className="print_width_height"
          sx={{
            maxHeight: `${fullScreenView ? "auto" : "calc(100vh - 250px)"}`,
            minHeight: "auto",
            overflow: "auto",
            position: "relative",
            minWidth: { xs: "400px", md: "800px" },
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#F2BE22", // Scrollbar color
              borderRadius: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "white",
            },
          }}
        >
          <table
            className={appStyles.table_common_container}
            style={{ position: "relative" }}
          >
            <thead style={{ position: "sticky", top: "0px" }}>
              {tableColumns?.map((each: any) => {
                if (each?.id === "startTime" || each?.id === "endTime") {
                  return <th className={styles.no_wrap}>{each?.fieldName}</th>;
                } else {
                  return <th>{each?.fieldName}</th>;
                }
              })}
            </thead>
            <tbody>
              {mySchedules?.map?.((row: any, index: number) => (
                <tr key={index}>
                  {tableColumns?.map((each: any) => {
                    if (each?.id !== "description") {
                      return (
                        <td style={{ whiteSpace: "nowrap" }}>
                          {row[each?.id]}
                        </td>
                      );
                    } else {
                      return (
                        <td style={{ wordBreak: "break-all" }}>
                          {row[each?.id]}
                        </td>
                      );
                    }
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Box>
    );
  };

  const renderButtonsComponent = () => {
    return (
      <Box
        sx={{ px: "20px", pb: "20px", display: { xs: "none", sm: "block" } }}
        className="no_print"
      >
        <CommonOutlinedButton
          onClick={handleClose}
          sx={{
            fontSize: { xs: "12px !important", sm: "14px !important" },
            height: "fit-content !important",
            p: { xs: "5px !important" },
            lineHeight: "1.45 !important",
          }}
        >
          Cancel
        </CommonOutlinedButton>
      </Box>
    );
  };

  const renderSCheduleData = () => {
    return (
      <Dialog
        onClose={handleClose}
        open={isAPICalled}
        PaperProps={{
          sx: { ...getDialogWidthAndHeight() },
        }}
        PaperComponent={DraggableFunction}
        aria-labelledby="draggable-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <Box
          className={styles.scheduling_container}
          ref={componentRef}
          sx={{ border: "none !important" }}
        >
          <Box
            className={styles.scheduling_title_container}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              cursor: "move",
            }}
          >
            <DialogTitle
              className={styles.scheduling_title}
              fontSize={{ xs: "15px", md: "18px" }}
              sx={{ p: "0px !important", flexGrow: 1 }}
            >
              {getPriestDisplayName()} Schedule{" "}
              <Box sx={{ display: { xs: "none", sm: "inline" } }}>
                (Period {displayDateFormat(new Date(formik?.values?.startDate))}{" "}
                to {displayDateFormat(new Date(formik?.values?.endDate))})
              </Box>
            </DialogTitle>
            <Box
              className="no_print"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <IconButton onClick={handleFullScreen} sx={{ p: "0px" }}>
                {!fullScreenView ? (
                  <Fullscreen sx={{ color: "white", cursor: "pointer" }} />
                ) : (
                  <FullscreenExit sx={{ color: "white", cursor: "pointer" }} />
                )}
              </IconButton>
              <IconButton onClick={handleClose} sx={{ p: "0px" }}>
                <Close sx={{ color: "white", cursor: "pointer", ml: "10px" }} />
              </IconButton>
            </Box>
          </Box>
          {renderDialogContentHeader()}
          {renderSchedulesTable()}
          {renderButtonsComponent()}
        </Box>
        {isExcelLoading && <PageLoader loading={isExcelLoading} />}
      </Dialog>
    );
  };

  return (
    <>
      {!isViewPriestSchedule && (
        <Typography className="sub_heading">My Schedule</Typography>
      )}
      {renderFieldsComponent()}
      {isAPICalled && (
        <>
          {mySchedules?.length > 0 ? (
            renderSCheduleData()
          ) : (
            <Typography
              className="sub_heading"
              sx={{ fontSize: "18px !important" }}
            >
              There are no schedules within the given date range.
            </Typography>
          )}
        </>
      )}
    </>
  );
};

export default MySchedule;
